import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import type Student from 'teacher-ui-client/models/student'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { t, type IntlService } from 'ember-intl'
import { differenceWith } from 'lodash'
import { Legend, TD, TH } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import formatValue from 'teacher-ui-client/helpers/format-value'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  id: string
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  totalAttempts: number | null
  quizzesTaken: number | null
  averageScore: number | null
}

export type QuizScoresHistoryDataItem = {
  student_id: number
  avg_score: number
  quizzes_taken: number
  total_attempts: number
}

interface Args {
  quizScoresHistoryData: QuizScoresHistoryDataItem[]
  students: Student[]
  uiScope: UIScopeName
  subco: boolean
  tooltip: string
  studentRouteName: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

interface ChartArgs {
  value: number
  showYAxisLabels: boolean
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    max: 100, // Set for 100%
    tickInterval: 25, // Show tick every 25%
    allowDecimals: false,
    opposite: true,
    labels: {
      enabled: chartOptions.showYAxisLabels,
      format: '{value}%',
    },
    offset: -10,
  }

  const series = [
    {
      type: 'bar' as const,
      name: 'averageScore',
      data: [chartOptions.value],
    },
  ]

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}%</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.wlBlue250],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class QuizScoresHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.quizScoresHistoryData
    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats?.find((stat) => String(stat.student_id) === student.id)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        id: student.id,
        studentId: student.id,
        quizzesTaken: statRow?.quizzes_taken ?? null,
        totalAttempts: statRow?.total_attempts ?? null,
        averageScore: statRow?.avg_score ?? null,
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.quizScoresHistoryData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildChartOptions({
          value: row.averageScore ?? 0,
          showYAxisLabels: index === 0,
        }),
      }
    })
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  <template>
    <div
      data-test-giraffe-quiz-scores-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-quiz-scores-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="flex overflow-x-auto print:block">
        <table data-test-history-table-quiz-scores class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-wl-blue-250 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "quizzesTaken")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.quizScores.quizzesTaken" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="quizzesTaken" />
                </div>
              </TH>
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "totalAttempts")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.quizScores.totalAttempts" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="totalAttempts" />
                </div>
              </TH>
              <TH
                class="w-24 font-bold"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "averageScore")}}
              >
                <div class="flex items-center justify-between font-bold">
                  {{t "reporting.charts.quizScores.avgScorePerQuiz" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="averageScore" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    border-none bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend data-test-quiz-scores-legend @title={{t "reporting.chartTitles.quizScoresHistory"}} />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-quiz-scores-student-row
                class="cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.id)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.quizzesTaken}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.totalAttempts}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.averageScore append="%"}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 cursor-default border-l px-0! py-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-quiz-scores-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default QuizScoresHistory
